import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X, Share, Plus } from 'lucide-react';

// Remember a dismissal for a while so we don't nag on every page load.
const DISMISS_KEY = 'zb_install_dismissed_at';
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

/**
 * Mobile-only install banner.
 *
 * On Android/Chrome it captures the `beforeinstallprompt` event and offers a
 * one-tap "Instalar" — the same flow as the header button, but visible without
 * digging through the menu (which is why the app felt like "just Chrome").
 * On iOS Safari (which has no install event) it shows the manual
 * "Compartir → Añadir a pantalla de inicio" instructions instead.
 *
 * Hidden when already running as an installed app, on desktop, or after a
 * recent dismissal.
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed — nothing to offer

    // Only phones/tablets; desktop installs are handled by the header button.
    if (!window.matchMedia('(max-width: 768px)').matches) return;

    // Respect a recent dismissal.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000) return;

    const ua = window.navigator.userAgent || '';
    const isIos = /iphone|ipad|ipod/i.test(ua);
    // iOS only allows install from Safari, not in-app/other browsers.
    const isIosSafari = isIos && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);

    if (isIos) {
      if (isIosSafari) { setIosHint(true); setShow(true); }
      return;
    }

    // Android / Chromium: wait for the install event, then reveal the banner.
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] sm:hidden px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md bg-white border border-[#e1e5e8] rounded-2xl shadow-2xl p-4 flex items-start gap-3">
        <img src="/icon-192.png" alt="zBilling" className="w-11 h-11 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#1d2d3d] text-sm leading-tight">{t('install.title', 'Instala zBilling en tu móvil')}</p>
          {iosHint ? (
            <p className="text-xs text-[#6d7d8b] mt-1 leading-snug">
              {t('install.iosPress', 'Pulsa')} <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#1a4f8b]" /> <b>{t('install.iosShare', 'Compartir')}</b> {t('install.iosThen', 'y luego')}
              <span className="inline-flex items-center gap-0.5"> <Plus className="inline w-3.5 h-3.5 text-[#1a4f8b]" /> <b>{t('install.iosAddHome', 'Añadir a pantalla de inicio')}</b></span>.
            </p>
          ) : (
            <p className="text-xs text-[#6d7d8b] mt-1 leading-snug">
              {t('install.body', 'Ábrela como una app: a pantalla completa, más rápida y con acceso directo.')}
            </p>
          )}
          {!iosHint && (
            <button
              onClick={install}
              className="mt-2.5 inline-flex items-center gap-1.5 bg-[#1a4f8b] hover:bg-[#123963] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
            >
              <Download className="w-4 h-4" /> {t('install.cta', 'Instalar app')}
            </button>
          )}
        </div>
        <button onClick={dismiss} aria-label={t('common.close', 'Cerrar')} className="text-[#9aa7b2] hover:text-[#6d7d8b] flex-shrink-0 -mt-0.5 -mr-0.5 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
